import { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { SearchSelect } from '../../components/SearchSelect';
import { Field, FormGrid, NumberInput, TextArea, TextInput } from '../../components/ui';
import { contractStatus, num } from '../../lib/format';
import { DELIVERABLE_CONTRACT_STATUSES, toNum } from '../../lib/sales';
import type { Contract, ContractLine, Delivery, DeliveryLine } from '../../lib/types';

function today(): string { return new Date().toISOString().slice(0, 10); }

interface WarehouseRow { id: string; code?: string; name_zh?: string; name_es?: string; }

// 交货单明细编辑态：数量以字符串受控，序列号用整段文本录入（换行 / 逗号分隔），保存前解析为数组。
interface DeliveryLineForm {
  key: string;
  contract_line_id: string;
  item_id?: string | null;
  description_es: string;
  uom: string;
  remaining: number;   // 该合同行剩余可交（来自合同：quantity - delivered_quantity）
  quantity: string;
  batch_number: string;
  serialText: string;
}

function parseSerials(text: string): string[] {
  return text.split(/[\n,，]/).map((s) => s.trim()).filter(Boolean);
}

function serialsToJson(raw?: string | null): string {
  if (!raw) return '';
  try { const arr = JSON.parse(raw); return Array.isArray(arr) ? arr.join('\n') : ''; } catch { return ''; }
}

export interface DeliveryEditorProps {
  initial?: Delivery | null;
  // 从合同列表「去交货」进入时预置来源合同。
  presetContractId?: string | null;
  onCancel: () => void;
  onSaved: (saved: Delivery) => void;
}

// 交货单新建 / 草稿编辑。新建：先选一份可交货合同 → 按「剩余可交」自动生成明细 → 存为草稿（不动库存）。
// 编辑：PATCH /api/deliveries/:id，仅草稿、仅本人（或管理员）。确认与出库副作用在详情页状态流转里执行。
export function DeliveryEditor({ initial, presetContractId, onCancel, onSaved }: DeliveryEditorProps) {
  const isEdit = !!initial?.id;
  const seq = useRef(1);
  const nextKey = () => `D${seq.current++}`;

  const [contractId, setContractId] = useState<string | null>(initial?.contract_id ?? presetContractId ?? null);
  const [contractLabel, setContractLabel] = useState<string>(initial?.contract_number ?? '');
  const [contract, setContract] = useState<Contract | null>(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractErr, setContractErr] = useState<string | null>(null);

  const [deliveryDate, setDeliveryDate] = useState(initial?.delivery_date?.slice(0, 10) || today());
  const [warehouseId, setWarehouseId] = useState<string | null>(initial?.warehouse_id ?? null);
  const [warehouseLabel, setWarehouseLabel] = useState('');
  const [carrier, setCarrier] = useState(initial?.carrier ?? '');
  const [tracking, setTracking] = useState(initial?.tracking_number ?? '');
  const [notesEs, setNotesEs] = useState(initial?.notes_es ?? '');
  // Remito R（RG 1415）抬头补充字段：均可为空，正式件的就绪由详情页「就绪检查」把关。
  const [poNo, setPoNo] = useState(initial?.po_no ?? '');
  const [issueDate, setIssueDate] = useState(initial?.issue_date?.slice(0, 10) || '');
  const [pointOfSale, setPointOfSale] = useState(initial?.point_of_sale ?? '');
  const [remitoConsecutive, setRemitoConsecutive] = useState(initial?.remito_consecutive ?? '');

  const [lines, setLines] = useState<DeliveryLineForm[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<string | null>(null);

  const isDeliverable = !contract || DELIVERABLE_CONTRACT_STATUSES.includes(contract.status);

  // 选中 / 预置合同后加载其明细，按剩余可交自动生成或回填编辑态。
  useEffect(() => {
    let cancelled = false;
    const cid = contractId;
    if (!cid) { setContract(null); return; }
    setContractLoading(true); setContractErr(null);
    api.detail<Contract>(`/api/contracts/${cid}`)
      .then((c) => {
        if (cancelled) return;
        setContract(c);
        setContractLabel((prev) => prev || c.number);
        if (!isEdit && c.po_no) setPoNo((prev) => prev || c.po_no || '');
        if (isEdit && initial?.lines?.length) {
          // 编辑草稿：用已存明细回填，remaining 取自合同当前值。
          const remainingById = new Map((c.lines ?? []).map((l) => [l.id, Number(l.quantity) - Number(l.delivered_quantity || 0)]));
          setLines(initial.lines.map((l: DeliveryLine) => ({
            key: nextKey(), contract_line_id: l.contract_line_id ?? '', item_id: l.item_id ?? null,
            description_es: l.description_es, uom: l.uom,
            remaining: remainingById.get(l.contract_line_id ?? '') ?? Number(l.quantity),
            quantity: String(l.quantity), batch_number: l.batch_number ?? '', serialText: serialsToJson(l.serial_numbers),
          })));
        } else {
          // 新建：为每条「剩余可交 > 0」的合同行生成一行，数量默认填剩余可交。
          const auto = (c.lines ?? [])
            .map((l: ContractLine) => ({ l, remaining: Number(l.quantity) - Number(l.delivered_quantity || 0) }))
            .filter(({ remaining }) => remaining > 1e-9)
            .map(({ l, remaining }) => ({
              key: nextKey(), contract_line_id: l.id, item_id: l.item_id ?? null, description_es: l.description_es,
              uom: l.uom, remaining, quantity: String(remaining), batch_number: '', serialText: '',
            }));
          setLines(auto);
        }
      })
      .catch((e) => { if (!cancelled) setContractErr(e instanceof ApiError ? e.message : '加载合同失败'); })
      .finally(() => { if (!cancelled) setContractLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const patchLine = (key: string, patch: Partial<DeliveryLineForm>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const grandQty = useMemo(() => lines.reduce((s, l) => s + toNum(l.quantity), 0), [lines]);

  const validate = (): boolean => {
    if (!contractId) { setFieldErr('请选择一份可交货的销售合同'); return false; }
    if (contract && contract.status === 'cancelled') { setFieldErr('来源合同已取消，不能交货'); return false; }
    if (!deliveryDate) { setFieldErr('交货日期为必填'); return false; }
    if (!lines.length) { setFieldErr('该合同没有可交货的明细行（可能已全部交货）'); return false; }
    const active = lines.filter((l) => toNum(l.quantity) > 0);
    if (!active.length) { setFieldErr('请至少填写一行交货数量（>0）'); return false; }
    for (const l of active) {
      const qty = toNum(l.quantity);
      if (qty > l.remaining + 1e-9) { setFieldErr(`「${l.description_es}」交货数量(${num(qty, 3)})超过剩余可交(${num(l.remaining, 3)})`); return false; }
      const serials = parseSerials(l.serialText);
      if (serials.length) {
        if (!Number.isInteger(qty)) { setFieldErr(`「${l.description_es}」填了序列号时，交货数量需为整数`); return false; }
        if (serials.length !== qty) { setFieldErr(`「${l.description_es}」序列号数量(${serials.length})与交货数量(${qty})不一致`); return false; }
      }
    }
    setFieldErr(null);
    return true;
  };

  const save = async () => {
    setErr(null);
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        contract_id: contractId,
        delivery_date: deliveryDate,
        warehouse_id: warehouseId || null,
        carrier: carrier || null,
        tracking_number: tracking || null,
        notes_es: notesEs || null,
        po_no: poNo || null,
        issue_date: issueDate || null,
        point_of_sale: pointOfSale || null,
        remito_consecutive: remitoConsecutive || null,
        lines: lines.filter((l) => toNum(l.quantity) > 0).map((l) => {
          const serials = parseSerials(l.serialText);
          return {
            contract_line_id: l.contract_line_id,
            quantity: toNum(l.quantity),
            batch_number: l.batch_number || null,
            ...(serials.length ? { serial_numbers: serials } : {}),
          };
        }),
      };
      const saved = isEdit
        ? await api.patch<{ data: Delivery }>(`/api/deliveries/${initial!.id}`, payload).then((r) => r.data)
        : await api.post<{ data: Delivery }>('/api/deliveries', { ...payload, status: 'draft' }).then((r) => r.data);
      onSaved(saved);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head">
        <div style={{ minWidth: 0 }}>
          <h2>{isEdit ? `编辑交货草稿 · ${initial!.number}` : '新建交货单（草稿）'}</h2>
          <div className="desc">
            选择可交货合同 → 按「剩余可交」自动生成明细 → 存为草稿（不动库存）。确认后才会出库并回写合同已交数量。
          </div>
        </div>
        <div className="spacer" />
        <span className="muted" style={{ fontSize: 12 }}>{isEdit ? 'PATCH /api/deliveries/:id' : 'POST /api/deliveries · status=draft'}</span>
      </div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        {contractErr ? <div className="notice bad" style={{ marginBottom: 12 }}>{contractErr}</div> : null}

        <FormGrid>
          <Field label="来源合同（仅可交货）" required span hint="下拉只列状态为已确认/生产中/待发货/部分交货、且仍有剩余可交数量的合同；支持按合同号或客户检索、加载更多。">
            <SearchSelect<Contract>
              endpoint="/api/contracts"
              params={{ deliverable: 1 }}
              limit={20}
              paginated
              value={contractId}
              label={contractLabel}
              disabled={isEdit}
              ariaLabel="选择可交货的销售合同"
              placeholder="按合同号 / 客户名称 / 编码检索可交货合同…"
              optionTitle={(c) => `${c.number} · ${c.customer_name || ''}`}
              optionMeta={(c) => `${contractStatus(c.status).label} · 剩余 ${num(c.remaining_qty ?? 0, 3)}`}
              onChange={(c) => { setContractId(c?.id ?? null); setContractLabel(c ? c.number : ''); }}
            />
          </Field>
          <Field label="交货日期" required>
            <TextInput type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
          </Field>
          <Field label="出库仓库" hint="草稿可暂不填，确认前必须指定">
            <SearchSelect<WarehouseRow>
              endpoint="/api/warehouses"
              value={warehouseId}
              label={warehouseLabel}
              ariaLabel="选择出库仓库"
              placeholder="输入仓库编码 / 名称检索…"
              optionTitle={(w) => w.name_es || w.name_zh || w.code || ''}
              optionMeta={(w) => w.code}
              onChange={(w) => { setWarehouseId(w?.id ?? null); setWarehouseLabel(w ? (w.name_es || w.name_zh || w.code || '') : ''); }}
            />
          </Field>
          <Field label="承运人">
            <TextInput value={carrier} placeholder="Transporte / Courier" onChange={(e) => setCarrier(e.target.value)} />
          </Field>
          <Field label="运单号">
            <TextInput value={tracking} placeholder="Nº de guía" onChange={(e) => setTracking(e.target.value)} />
          </Field>
          <Field label="客户 PO 号" hint="采购订单号（沿用合同，可覆盖）。留空则不打印。">
            <TextInput value={poNo} placeholder="Orden de compra (PO)" onChange={(e) => setPoNo(e.target.value)} />
          </Field>
          <Field label="签发日期" hint="Fecha de emisión。留空默认取交货日期。">
            <TextInput type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </Field>
          <Field label="销售点（Punto de Venta）" hint="正式 Remito R 需要；草稿 / 内部预览可留空。">
            <TextInput value={pointOfSale} placeholder="00001" onChange={(e) => setPointOfSale(e.target.value)} />
          </Field>
          <Field label="Remito 连续编号（Nº consecutivo）" hint="与 P.V. 组成法定编号，不等同内部 REM 号；正式件必填。">
            <TextInput value={remitoConsecutive} placeholder="00001234" onChange={(e) => setRemitoConsecutive(e.target.value)} />
          </Field>
        </FormGrid>

        {contractLoading ? <div className="notice info" style={{ marginTop: 12 }}>读取合同明细…</div> : null}
        {contract && !isDeliverable ? (
          <div className="notice warn" style={{ marginTop: 12 }}>该合同当前状态为「{contract.status}」，通常不可继续交货；如需强制请回到合同推进状态。</div>
        ) : null}
        {fieldErr ? <div className="notice warn" style={{ margin: '12px 0 0' }}>{fieldErr}</div> : null}

        {contractId && !contractLoading ? (
          <div style={{ marginTop: 16 }}>
            <div className="detail-head" style={{ marginBottom: 8 }}>
              <h3 style={{ fontSize: 14 }}>交货明细（按剩余可交）</h3>
              <div className="spacer" />
              {contract ? <span className="muted" style={{ fontSize: 12 }}>{contract.number} · 客户 {contract.customer_name || '—'}</span> : null}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="line-grid">
                <thead>
                  <tr>
                    <th style={{ minWidth: 200 }}>描述</th>
                    <th style={{ width: 96 }} className="col-r">剩余可交</th>
                    <th style={{ width: 92 }} className="col-r">本次数量</th>
                    <th style={{ width: 110 }}>批次</th>
                    <th style={{ minWidth: 200 }}>实物序列号（登记过工件的合同必填）</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr><td colSpan={5} className="muted" style={{ padding: '14px 8px' }}>该合同没有剩余可交的明细（可能已全部交货）。</td></tr>
                  ) : lines.map((l) => {
                    const serials = parseSerials(l.serialText);
                    const qty = toNum(l.quantity);
                    const mismatch = serials.length > 0 && serials.length !== qty;
                    const registered = contract?.lines?.find((cl) => cl.id === l.contract_line_id)?.assets ?? [];
                    const registeredSerials = registered.map((a) => a.customer_serial).filter((s): s is string => !!s);
                    return (
                      <tr key={l.key}>
                        <td><span title={l.description_es}>{l.description_es}</span> <span className="muted" style={{ fontSize: 11 }}>({l.uom})</span></td>
                        <td className="col-r"><span className="num muted">{num(l.remaining, 3)}</span></td>
                        <td><NumberInput value={l.quantity} onChange={(e) => patchLine(l.key, { quantity: e.target.value })} /></td>
                        <td><TextInput value={l.batch_number} placeholder="Lote" onChange={(e) => patchLine(l.key, { batch_number: e.target.value })} /></td>
                        <td>
                          <TextArea
                            style={{ minHeight: 52, fontFamily: 'var(--mono, ui-monospace, monospace)' }}
                            value={l.serialText}
                            placeholder={registeredSerials.length
                              ? `本行已登记：${registeredSerials.join('、')}\n填本次实际交付的序列号，每行一个`
                              : 'SN-001\nSN-002（无登记工件时可留空）'}
                            onChange={(e) => patchLine(l.key, { serialText: e.target.value })}
                          />
                          {registeredSerials.length ? (
                            <span className="muted" style={{ fontSize: 11 }}>
                              确认后系统按序列号自动关联已登记工件、维修版本及质检单；该行仅登记一件且数量为 1 时可自动匹配。
                            </span>
                          ) : null}
                          {serials.length > 0 ? (
                            <span className={`muted${mismatch ? ' bad' : ''}`} style={{ fontSize: 11 }}>
                              已录 {serials.length} 个序列号{mismatch ? `，与数量 ${qty} 不一致` : ''}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="editor-totals">
              <div className="t"><span className="k">本单交货总数量</span><span className="v">{num(grandQty, 3)}</span></div>
            </div>
            {lines.some((l) => toNum(l.quantity) > l.remaining + 1e-9) ? (
              <div className="notice bad" style={{ marginTop: 8 }}>存在超过剩余可交数量的明细，后端将强制拒绝（禁止超交 / 重复交付）。</div>
            ) : null}
          </div>
        ) : null}

        <Field label="西语备注（REMITO 打印展示）" span>
          <TextArea value={notesEs} placeholder="Observaciones para el remito…" onChange={(e) => setNotesEs(e.target.value)} />
        </Field>

        <div className="detail-actions" style={{ marginTop: 16 }}>
          <button className="btn primary" onClick={save} disabled={busy || !contractId || contractLoading}>
            {busy ? '保存中…' : isEdit ? '保存草稿' : '创建草稿'}
          </button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
          <span className="muted" style={{ fontSize: 11.5, marginLeft: 6 }}>保存后为草稿，需在列表行内「确认交货」才会出库并推进合同进度。</span>
        </div>
      </div>
    </section>
  );
}
